import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../../../shared/Locator';
import { getProjectBaseURLFromParams, nuc } from '../../../../../utils';
import DynamicShowMoreList from '../../../../generic/DynamicShowMoreList';
import RouterLink from '../../../../generic/Link/RouterLink';
import type { Dependency, DependencyRootProject } from '../../../../../../shared/dependenciesV2';

import styles from './DependencyRootProjects.module.less';

// Based on the current styling, this is the height of a single root projects row.
const ROOT_PROJECTS_LINE_HEIGHT = 21;

export type Props = {
  dependency: Dependency;
  rootProjects: DependencyRootProject[];
};

/**
 * Displays the root projects linked to a Release Group's dependency (root projects
 * are the projects in the Release Group that led to the inclusion of this dependnecy).
 * This component hides any projects beyond the first row to avoid cluttering the UI.
 * If enough projects are present, the "show/hide" control will appear that enables
 * expanding and collapsing of the list.
 */
const DependencyRootProjects: VoidFunctionComponent<Props> = ({ dependency, rootProjects }) => {
  // Determine the specific base URL for each root project.
  const projects = rootProjects.map((project) => {
    const locator = new Locator(project.revision);
    const url = getProjectBaseURLFromParams({
      project_id: locator.toPackageString(),
      reference_type: 'branch',
      reference: project.branch,
      revision_id: locator.revision.value ?? '',
    });
    return {
      title: project.title,
      // When linking from a release group dependency to a root project, pass the dependency's
      // locator as a query parameter to filter the project's dependencies to just this one.
      url: `${url}/browse/dependencies?title=${nuc(dependency.locator)}`,
    };
  });

  // Sort root projects by title for consistent display.
  const sortedProjects = _.sortBy(projects, (project) => project.title);
  const sortedProjectElements = sortedProjects.map((project, index) => (
    <span>
      <RouterLink to={project.url}>{project.title}</RouterLink>
      {index < projects.length - 1 && (<span>, </span>)}
    </span>
  ));

  return (
    <div className={styles.rootProjects}>
      <span className={styles.rootProjectsList}>
        <DynamicShowMoreList list={sortedProjectElements} lineHeight={ROOT_PROJECTS_LINE_HEIGHT} />
      </span>
    </div>
  );
};

export default DependencyRootProjects;
